import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { formatDateDMY } from "../lib/ScheduleUtils";
import { isBookableDoctor, getOrganizationDisplayName } from "../utils/utils";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import Button from "./common/Button";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  ArrowLeft,
  Clock,
  Users,
  Stethoscope,
  Loader2,
} from "lucide-react";
import * as utils from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function InstitutionLanding() {
  const { t } = useBrandTranslation();
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    if (institutionId) {
      loadInstitutionData();
    }
  }, [institutionId]);

  const loadInstitutionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos de la institución
      const { data: institutionData, error: institutionError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", institutionId)
        .in("role", ["institution", "secretary"])
        .maybeSingle();

      if (institutionError) throw institutionError;

      if (!institutionData) {
        setError("Institución no encontrada");
        setLoading(false);
        return;
      }

      setInstitution(institutionData);

      // Cargar doctores asociados a esta institución
      await loadAssociatedDoctors(institutionData.id);
    } catch (err) {
      console.error("Error loading institution:", err);
      setError("Error al cargar los datos de la institución");
    } finally {
      setLoading(false);
    }
  };

  const loadAssociatedDoctors = async (secretaryId) => {
    try {
      // Buscar shared_schedules asociados a esta secretaria
      const { data: sharedSchedules, error: sharedError } = await supabase
        .from("shared_schedules")
        .select("schedule_id")
        .eq("secretary_id", secretaryId);

      if (sharedError) throw sharedError;

      if (!sharedSchedules || sharedSchedules.length === 0) {
        setDoctors([]);
        return;
      }

      const scheduleIds = sharedSchedules.map((s) => s.schedule_id);

      // Obtener los schedules y sus doctor_ids
      const { data: schedules, error: schedulesError } = await supabase
        .from("schedules")
        .select("doctor_id")
        .in("id", scheduleIds);

      if (schedulesError) throw schedulesError;

      const doctorIds = [...new Set(schedules.map((s) => s.doctor_id))];

      if (doctorIds.length === 0) {
        setDoctors([]);
        return;
      }

      // Obtener los perfiles de los doctores
      const { data: doctorsData, error: doctorsError } = await supabase
        .from("profiles")
        .select("*")
        .in("id", doctorIds)
        .eq("role", "doctor");

      if (doctorsError) throw doctorsError;

      setDoctors((doctorsData || []).filter(isBookableDoctor));
    } catch (err) {
      console.error("Error loading associated doctors:", err);
      // No mostramos error aquí, solo no mostramos doctores
      setDoctors([]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t("app.landing.loadingInstitution", "Cargando información de la institución...")}</p>
        </div>
      </div>
    );
  }

  if (error || !institution) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.commonUi.error", "Error")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              {error || "Institución no encontrada"}
            </p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const institutionName = getOrganizationDisplayName(institution);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header con botón volver */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>

        {/* Hero Section */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <Building2 className="w-16 h-16" />
              <div>
                <h1 className="text-4xl font-bold mb-2">{institutionName}</h1>
                {institution.role === "institution" && (
                  <p className="text-blue-100 text-lg">{t("app.landing.medicalInstitution", "Institución Médica")}</p>
                )}
                {institution.role === "secretary" && (
                  <p className="text-blue-100 text-lg">{t("app.landing.medicalCenter", "Centro Médico")}</p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Información de contacto */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600" />
                Contacto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {institution.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.profile.phone", "Teléfono")}</p>
                    <a
                      href={`tel:${institution.phone}`}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {institution.phone}
                    </a>
                  </div>
                </div>
              )}

              {institution.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.profile.email", "Email")}</p>
                    <a
                      href={`mailto:${institution.email}`}
                      className="text-blue-600 hover:text-blue-800 font-medium break-all"
                    >
                      {institution.email}
                    </a>
                  </div>
                </div>
              )}

              {institution.name && institution.role === "secretary" && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.profile.contact", "Contacto")}</p>
                    <p className="text-gray-900 font-medium">
                      {institution.name} {institution.last_name}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Información
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">{t("app.landing.memberSince", "Miembro desde")}</p>
                  <p className="text-gray-900 font-medium">
                    {formatDateDMY(institution.created_at)}
                  </p>
                </div>
              </div>

              {doctors.length > 0 && (
                <div className="flex items-center gap-3">
                  <Stethoscope className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">{t("app.landing.associatedProfessionals", "Profesionales asociados")}</p>
                    <p className="text-gray-900 font-medium">{doctors.length}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Profesionales asociados */}
        {doctors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-blue-600" />
                Profesionales Asociados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {doctors.map((doctor) => (
                  <div
                    key={doctor.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="font-semibold text-lg mb-1">
                      Dr. {doctor.name} {doctor.last_name}
                    </h3>
                    {doctor.specialization && (
                      <p className="text-sm text-gray-600 mb-2">
                        {doctor.specialization}
                      </p>
                    )}
                    {doctor.email && (
                      <p className="text-xs text-gray-500">{doctor.email}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Acciones */}
        <div className="mt-6 flex gap-4 justify-center">
          <Button
            onClick={() => navigate("/patient/search")}
            className="flex items-center gap-2"
          >
            <Stethoscope className="w-4 h-4" />
            Buscar Profesionales
          </Button>
        </div>
      </div>
    </div>
  );
}

